import type { MutableRefObject } from 'react';
import type { WebSocketClient } from '../lib/websocket';
import type { InterviewWebSocketControllers } from './interviewWebSocketControllerFactory';

export interface InterviewWebSocketEventRegistrarInput {
  wsClient: Pick<WebSocketClient, 'on' | 'setBinaryHandler'>;
  controllers: InterviewWebSocketControllers;
  sessionIdRef: MutableRefObject<string | null>;
  debugLog: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
}

/** 面接WebSocketのイベント名と各コントローラの対応だけを登録する。 */
export function registerInterviewWebSocketEvents(
  input: InterviewWebSocketEventRegistrarInput,
): void {
  registerResponseEvents(input);
  registerConnectionAndStateEvents(input);
  registerSttEvents(input);
  registerErrorEvents(input);
}

function registerResponseEvents(input: InterviewWebSocketEventRegistrarInput): void {
  const { wsClient, controllers } = input;
  const { responseMessages } = controllers;

  wsClient.setBinaryHandler((data) => responseMessages.handleBinary(data));
  wsClient.on('video_start', (message) => {
    responseMessages.handleVideoStart(message.payload || {});
  });
  wsClient.on('video_end', (message) => {
    responseMessages.handleVideoEnd(message.payload || {});
  });
  wsClient.on('segment_text', (message) => {
    responseMessages.handleSegmentText(message.payload || {});
  });
}

function registerConnectionAndStateEvents(input: InterviewWebSocketEventRegistrarInput): void {
  const { wsClient, controllers, debugLog } = input;
  const { connectionLifecycle, stateMessages } = controllers;

  wsClient.on('connected', (message) => {
    debugLog('[WebSocket] Connected:', message.payload);
    connectionLifecycle.handleConnected(message.payload || {});
  });
  wsClient.on('fsm_initialized', (message) => {
    stateMessages.handleFsmInitialized(message.payload || {});
  });
  wsClient.on('patient_tracker_initialized', (message) => {
    // connect_session確定応答後にだけSTT再開を試みる既存順序を維持する。
    stateMessages.handlePatientTrackerInitialized(message.payload || {});
  });
  wsClient.on('patient_state_update', (message) => {
    stateMessages.handlePatientStateUpdate(message.payload || {});
  });
}

function registerSttEvents(input: InterviewWebSocketEventRegistrarInput): void {
  const {
    wsClient,
    controllers,
    sessionIdRef,
    debugLog,
    warn,
  } = input;
  const { sttMessages, sttRecovery, markSttAlive } = controllers;

  wsClient.on('recognition_started', () => {
    debugLog('[WebSocket] recognition_started ack received; STT confirmed alive');
    markSttAlive();
  });

  debugLog('[Client] Registering stt_interim handler...');
  wsClient.on('stt_interim', (message) => {
    debugLog('[Client] stt_interim handler called!', message);
    debugLog('[Client] stt_interim received:', message.payload);
    sttMessages.handleInterim(message.payload || {});
  });
  wsClient.on('stt_final', (message) => {
    sttMessages.handleFinal('stt_final', message.payload || {});
  });
  wsClient.on('stt_result', (message) => {
    sttMessages.handleFinal('stt_result', message.payload || {});
  });
  wsClient.on('recognition_session_missing', (message) => {
    const { sessionId, message: errorMessage } = message.payload || {};
    const sessionMismatch = typeof sessionId === 'string'
      && sessionIdRef.current
      && sessionId !== sessionIdRef.current;
    if (sessionMismatch) {
      warn(
        '[WebSocket] recognition_session_missing with mismatched sessionId, proceeding to recover anyway:',
        { incoming: sessionId, current: sessionIdRef.current },
      );
    }

    warn('[WebSocket] recognition_session_missing:', message.payload);
    void sttRecovery.handleSessionMissing(
      typeof errorMessage === 'string' ? errorMessage : undefined,
    );
  });
}

function registerErrorEvents(input: InterviewWebSocketEventRegistrarInput): void {
  const { wsClient, controllers } = input;
  wsClient.on('error', (message) => {
    controllers.webSocketErrors.handle(message.payload || {});
  });
}
